import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { PromptItem, Category } from './types';

interface PromptDB extends DBSchema {
  prompts: {
    key: string;
    value: PromptItem;
  };
  categories: {
    key: string;
    value: Category;
  };
  settings: {
    key: string;
    value: any;
  };
}

class StorageManager {
  private db: IDBPDatabase<PromptDB> | null = null;
  private dbName = 'PromptDB';
  private dbVersion = 1;

  async init(): Promise<void> {
    if (this.db) return;

    this.db = await openDB<PromptDB>(this.dbName, this.dbVersion, {
      upgrade(db) {
        // 创建prompts对象存储
        if (!db.objectStoreNames.contains('prompts')) {
          db.createObjectStore('prompts', { keyPath: 'id' });
        }

        // 创建categories对象存储
        if (!db.objectStoreNames.contains('categories')) {
          db.createObjectStore('categories', { keyPath: 'id' });
        }

        // 创建settings对象存储
        if (!db.objectStoreNames.contains('settings')) {
          db.createObjectStore('settings');
        }
      },
    });
  }

  private async ensureDB(): Promise<IDBPDatabase<PromptDB>> {
    if (!this.db) {
      await this.init();
    }
    return this.db!;
  }

  // Prompts相关方法
  async getPrompts(): Promise<PromptItem[]> {
    const db = await this.ensureDB();
    return await db.getAll('prompts');
  }

  async getPrompt(id: string): Promise<PromptItem | undefined> {
    const db = await this.ensureDB();
    return await db.get('prompts', id);
  }

  async savePrompt(prompt: PromptItem): Promise<void> {
    const db = await this.ensureDB();
    await db.put('prompts', prompt);
  }

  async savePrompts(prompts: PromptItem[]): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('prompts', 'readwrite');
    // 先清空所有提示词
    await tx.store.clear();
    // 然后保存新的提示词
    await Promise.all(prompts.map(prompt => tx.store.put(prompt)));
    await tx.done;
  }

  async deletePrompt(id: string): Promise<void> {
    const db = await this.ensureDB();
    await db.delete('prompts', id);
  }

  async clearPrompts(): Promise<void> {
    const db = await this.ensureDB();
    await db.clear('prompts');
  }

  // Categories相关方法
  async getCategories(): Promise<Category[]> {
    const db = await this.ensureDB();
    return await db.getAll('categories');
  }

  async getCategory(id: string): Promise<Category | undefined> {
    const db = await this.ensureDB();
    return await db.get('categories', id);
  }

  async saveCategory(category: Category): Promise<void> {
    const db = await this.ensureDB();
    await db.put('categories', category);
  }

  async saveCategories(categories: Category[]): Promise<void> {
    const db = await this.ensureDB();
    const tx = db.transaction('categories', 'readwrite');
    // 先清空所有分类
    await tx.store.clear();
    // 然后保存新的分类
    await Promise.all(categories.map(category => tx.store.put(category)));
    await tx.done;
  }

  async deleteCategory(id: string): Promise<void> {
    const db = await this.ensureDB();
    await db.delete('categories', id);
  }

  async clearCategories(): Promise<void> {
    const db = await this.ensureDB();
    await db.clear('categories');
  }

  // Settings相关方法
  async getSetting<T>(key: string): Promise<T | undefined> {
    const db = await this.ensureDB();
    return await db.get('settings', key);
  }

  async setSetting<T>(key: string, value: T): Promise<void> {
    const db = await this.ensureDB();
    await db.put('settings', value, key);
  }

  async deleteSetting(key: string): Promise<void> {
    const db = await this.ensureDB();
    await db.delete('settings', key);
  }

  async clearSettings(): Promise<void> {
    const db = await this.ensureDB();
    await db.clear('settings');
  }

  // 通用方法
  async clearAll(): Promise<void> {
    const db = await this.ensureDB();
    await Promise.all([
      db.clear('prompts'),
      db.clear('categories'), 
      db.clear('settings')
    ]);
  }

  // 监听存储变化的事件发射器
  private listeners: Map<string, Set<(data: any) => void>> = new Map();

  addEventListener(event: string, callback: (data: any) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(callback);
  }

  removeEventListener(event: string, callback: (data: any) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(callback);
    }
  }

  private emitEvent(event: string, data: any): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach(callback => callback(data));
    }
  }

  // 重写save方法以发射事件
  async savePromptWithEvent(prompt: PromptItem): Promise<void> {
    await this.savePrompt(prompt);
    this.emitEvent('promptsChanged', await this.getPrompts());
  }

  async savePromptsWithEvent(prompts: PromptItem[]): Promise<void> {
    await this.savePrompts(prompts);
    this.emitEvent('promptsChanged', prompts);
  }

  async deletePromptWithEvent(id: string): Promise<void> {
    await this.deletePrompt(id);
    this.emitEvent('promptsChanged', await this.getPrompts());
  }

  // 清空所有数据
  async clearAllData(): Promise<void> {
    try {
      if (!this.db) {
        await this.init();
      }

      if (!this.db) {
        throw new Error('数据库初始化失败');
      }

      // 清空所有数据表
      const tx = this.db.transaction(['prompts', 'categories', 'settings'], 'readwrite');
      await Promise.all([
        tx.objectStore('prompts').clear(),
        tx.objectStore('categories').clear(),
        tx.objectStore('settings').clear(),
      ]);

      console.log('所有数据已清空');
      
      // 发射存储变化事件
      this.emitEvent('promptsChanged', []);
    } catch (error) {
      console.error('清空数据失败:', error);
      throw error;
    }
  }
}

// 创建单例实例
export const storageManager = new StorageManager();

// 兼容原有的browser storage API的包装器
export const storage = {
  local: {
    get: async (keys: string | string[] | Record<string, any>): Promise<Record<string, any>> => {
      await storageManager.init();
      
      if (typeof keys === 'string') {
        const value = await storageManager.getSetting(keys);
        return { [keys]: value };
      }
      
      if (Array.isArray(keys)) {
        const result: Record<string, any> = {};
        for (const key of keys) {
          result[key] = await storageManager.getSetting(key);
        }
        return result;
      }
      
      // 如果是对象，返回默认值
      const result: Record<string, any> = {};
      for (const [key, defaultValue] of Object.entries(keys)) {
        const value = await storageManager.getSetting(key);
        result[key] = value !== undefined ? value : defaultValue;
      }
      return result;
    },
    
    set: async (items: Record<string, any>): Promise<void> => {
      await storageManager.init();
      
      for (const [key, value] of Object.entries(items)) {
        await storageManager.setSetting(key, value);
      }
    },
    
    remove: async (keys: string | string[]): Promise<void> => {
      await storageManager.init();
      
      const keyArray = Array.isArray(keys) ? keys : [keys];
      for (const key of keyArray) {
        await storageManager.deleteSetting(key);
      }
    },
    
    clear: async (): Promise<void> => {
      await storageManager.init();
      await storageManager.clearSettings();
    }
  },
  
  sync: {
    get: async (keys: string | string[] | Record<string, any>): Promise<Record<string, any>> => {
      // 对于Web应用，sync和local使用相同的存储
      return storage.local.get(keys);
    },
    
    set: async (items: Record<string, any>): Promise<void> => {
      return storage.local.set(items);
    },
    
    remove: async (keys: string | string[]): Promise<void> => {
      return storage.local.remove(keys);
    },
    
    clear: async (): Promise<void> => {
      return storage.local.clear();
    }
  },
  
  // 存储变化监听器
  onChanged: {
    addListener: (callback: (changes: Record<string, any>, areaName: string) => void): void => {
      const wrappedCallback = (data: { changes: Record<string, any>, areaName: string }) => {
        callback(data.changes, data.areaName);
      };
      storageManager.addEventListener('storageChanged', wrappedCallback);
    },
    
    removeListener: (_callback: (changes: Record<string, any>, areaName: string) => void): void => {
      // 注意：这里无法准确移除包装后的回调，但在Web应用中通常不需要
      console.warn('removeListener not fully implemented in web storage adapter');
    }
  }
};
